import React, { useEffect, useMemo, useState } from "react";
import { createStructuredSelector } from "reselect";
import { connect } from "react-redux";
import { isEmpty } from "lodash";

import { getLabel } from "utils/dcfExport/details";
import { getOptions } from "utils/fields";
import convertUnit from "utils/convertUnit";
import { getNetTotalEnergyConsumption } from "utils/results";
import { getClimateZone } from "utils/buildingCodesPrograms";

import { actions as modelActions } from "features/Model/_ducks";
import { selectNzStudyReportPdfData, selectModelData } from "features/Model/_ducks/selectors";
import { selectResults } from "features/Model/Review/Results/_ducks/selectors";

import Input from "components/Input";
import Button from "components/Button";
import InputSectionContainer from "components/PdfExport/InputSectionContainer";

const { updateNzStudyPdfData } = modelActions;

const TopLevelContent = ({
    results,
    modelData,
    nzStudyReportPdfData = {},
    updateNzStudyPdfData,
    eFileResults = {},
}) => {
    const {
        selectedUpgrade = "",
        nzPage1Content = {},
    } = nzStudyReportPdfData;

    const [currentSelectedUpgrade, setCurrentSelectedUpgrade] = useState(selectedUpgrade);

    // Determine if selected upgrade is from E-file
    const isEFileUpgrade = selectedUpgrade.startsWith("eFile_");
    const actualUpgradeKey = isEFileUpgrade ? selectedUpgrade.replace("eFile_", "") : selectedUpgrade;

    const upgradeCaseKey = isEFileUpgrade && actualUpgradeKey === "base" ? "socBaseCase" : "socAllUpgrades";

    // Use E-file data if selected upgrade is from E-file, otherwise use current model data
    const postRetrofitPackageResults = isEFileUpgrade ? eFileResults : results?.packageResults || {};

    const { modelData: preRetrofitModelDataFromResults = {}, resultsData: preRetrofitResultsData = {} } =
        results?.packageResults?.base || {};

    const { modelData: postRetrofitModelDataFromResults = {}, resultsData: postRetrofitResultsData = {} } =
        postRetrofitPackageResults[actualUpgradeKey] || {};

    //all of this comes from the pre retrofit file
    const {
        chbaNz: { file: { renovatorCompany = "", targetLabel = {}, targetCompletionDate = "" } = {} } = {}, //pre retrofit file
        h2kVersion: { major = 0, minor = 0 } = {}, //pre retrofit file
    } = modelData || {};

    const h2kVersion = `v${major}-${minor}`;

    const performanceId = targetLabel.id ?? 0; //pre retrofit file

    const {
        specifications: {
            yearBuilt: { value: yearBuilt = "" } = {}, //pre retrofit file
            buildingType: { id: buildingTypeId = 0 } = {}, //pre retrofit file
        } = {},
        location: {
            //pre retrofit file
            weather: {
                region: { id: regionId = 0 } = {},
                location: {
                    // Location
                    id: locationId = 0,
                    // HDD
                    value: heatingDegreeDay = 0,
                } = {},
            } = {},
        } = {},
    } = modelData || {};

    useEffect(() => {
        // Fill default content if the selected upgrade is changed or page content is empty
        if (isEmpty(nzPage1Content) || currentSelectedUpgrade !== selectedUpgrade) {
            restoreDefaultContent();
            setCurrentSelectedUpgrade(selectedUpgrade);
        }
    }, [selectedUpgrade]);

    const restoreDefaultContent = () => {
        const initContent = {
            nzRenovator: renovatorCompany ? renovatorCompany : "N/A",
            nzEAOrg: nzPage1Content.nzEAOrg || "N/A", // Keep user changes
            nzPerformance: selectedLabel ? selectedLabel.replace(" Label", "") : "N/A",
            nzEnergyReduction: energyReduction ? `${energyReduction}%` : "N/A",
            nzYearBuilt: yearBuilt ? yearBuilt : "N/A",
            nzCompletionYear: targetCompletionDate ? targetCompletionDate.split("-")[0] : "N/A",
            nzLocation: `${city}, ${province}`,
            nzBuildingType: buildingType ? buildingType : "N/A",
            nzBuildingSize: `${convertUnit({
                value: aboveGradeIntFloorAreaTotal + belowGradeIntFloorAreaTotal,
                type: "area",
                inputUnit: "m2",
                outputUnit: "ft2",
            }).toFixed(0)} sq.ft.`,
            nzClimate: `Zone ${getClimateZone(heatingDegreeDay)}, ${heatingDegreeDay} HDD`,
        };
        updateNzStudyPdfData("nzPage1Content", initContent);
    };

    const {
        dimensions: {
            aboveGradeIntFloorArea: { total: aboveGradeIntFloorAreaTotal = 0 } = {}, //post retrofit file
            belowGradeIntFloorArea: { total: belowGradeIntFloorAreaTotal = 0 } = {}, //post retrofit file
        } = {},
    } = postRetrofitModelDataFromResults || {};

    const energyReduction = useMemo(() => {
        const { zeroFloorTotal: netTotalConsumption = 0 } = getNetTotalEnergyConsumption(
            postRetrofitResultsData[upgradeCaseKey] || {}
        );
        const { zeroFloorTotal: netBaseConsumption = 0 } = getNetTotalEnergyConsumption(
            preRetrofitResultsData["socBaseCase"] || {}
        );

        return ((100 * (netBaseConsumption - netTotalConsumption)) / netBaseConsumption).toFixed(1);
    }, [preRetrofitResultsData, postRetrofitResultsData]);

    const buildingType = useMemo(() => getLabel("buildingSubType", buildingTypeId), [buildingTypeId]);
    const province = useMemo(() => getLabel("weatherRegion", regionId, "default", h2kVersion), [regionId, h2kVersion]);
    const city = useMemo(
        () => getLabel("weatherLocation", locationId, regionId, h2kVersion),
        [locationId, regionId, h2kVersion]
    );

    const chbaLabelOptions = getOptions({ fieldKey: "chbaNzTargetLabel" });
    const selectedLabel = chbaLabelOptions.find((opt) => opt?.value?.id === performanceId)?.label || "";

    return (
        <InputSectionContainer title={"Top-level Content"} info={"Default content was taken from the current model's data and the selected upgrade package."}>
            <Button onClick={restoreDefaultContent}>
                Restore Defaults
            </Button>
            <Input
                label={"Renovator"}
                placeholder="Enter renovator company"
                input={{
                    value: nzPage1Content.nzRenovator,
                    onChange: (value) => {
                        const newContent = { ...nzPage1Content, "nzRenovator": value };
                        updateNzStudyPdfData("nzPage1Content", newContent);
                    },
                }}
            />
            <Input
                label={"Energy Advisor/Service Organization"}
                placeholder="Enter organization"
                input={{
                    value: nzPage1Content.nzEAOrg,
                    onChange: (value) => {
                        const newContent = { ...nzPage1Content, "nzEAOrg": value };
                        updateNzStudyPdfData("nzPage1Content", newContent);
                    },
                }}
            />
            <Input
                label={"Performance"}
                placeholder="Enter performance"
                input={{
                    value: nzPage1Content.nzPerformance,
                    onChange: (value) => {
                        const newContent = { ...nzPage1Content, "nzPerformance": value };
                        updateNzStudyPdfData("nzPage1Content", newContent);
                    },
                }}
            />
            <Input
                label={"Energy Reduction"}
                placeholder="Enter energy reduction"
                input={{
                    value: nzPage1Content.nzEnergyReduction,
                    onChange: (value) => {
                        const newContent = { ...nzPage1Content, "nzEnergyReduction": value };
                        updateNzStudyPdfData("nzPage1Content", newContent);
                    },
                }}
            />
            <Input
                label={"Year Built"}
                placeholder="Enter year built"
                input={{
                    value: nzPage1Content.nzYearBuilt,
                    onChange: (value) => {
                        const newContent = { ...nzPage1Content, "nzYearBuilt": value };
                        updateNzStudyPdfData("nzPage1Content", newContent);
                    },
                }}
            />
            <Input
                label={"Year Retrofit Completed"}
                placeholder="Enter target completion date"
                input={{
                    value: nzPage1Content.nzCompletionYear,
                    onChange: (value) => {
                        const newContent = { ...nzPage1Content, "nzCompletionYear": value };
                        updateNzStudyPdfData("nzPage1Content", newContent);
                    },
                }}
            />
            <Input
                label={"Location"}
                placeholder="Enter location"
                input={{
                    value: nzPage1Content.nzLocation,
                    onChange: (value) => {
                        const newContent = { ...nzPage1Content, "nzLocation": value };
                        updateNzStudyPdfData("nzPage1Content", newContent);
                    },
                }}
            />
            <Input
                label={"House Type"}
                placeholder="Enter building type"
                input={{
                    value: nzPage1Content.nzBuildingType,
                    onChange: (value) => {
                        const newContent = { ...nzPage1Content, "nzBuildingType": value };
                        updateNzStudyPdfData("nzPage1Content", newContent);
                    },
                }}
            />
            <Input
                label={"Size"}
                placeholder="Enter building size"
                input={{
                    value: nzPage1Content.nzBuildingSize,
                    onChange: (value) => {
                        const newContent = { ...nzPage1Content, "nzBuildingSize": value };
                        updateNzStudyPdfData("nzPage1Content", newContent);
                    },
                }}
            />
            <Input
                label={"Climate"}
                placeholder="Enter climate"
                input={{
                    value: nzPage1Content.nzClimate,
                    onChange: (value) => {
                        const newContent = { ...nzPage1Content, "nzClimate": value };
                        updateNzStudyPdfData("nzPage1Content", newContent);
                    },
                }}
            />
        </InputSectionContainer>
    );
};

const mapStateToProps = createStructuredSelector({
    results: selectResults,
    modelData: selectModelData,
    nzStudyReportPdfData: selectNzStudyReportPdfData,
});

const mapDispatchToProps = (dispatch) => ({
    updateNzStudyPdfData: (fieldName, value) => dispatch(updateNzStudyPdfData(fieldName, value)),
});

export default connect(mapStateToProps, mapDispatchToProps)(TopLevelContent);